import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import Loader from "components/Loader";
import { setScrollBehavior } from "features/scrollPageBehaviorProvider/scrollPageBehaviorProvider";
import { TeamGroupForm } from "features/teamGroup/TeamGroupForm";
import { useTeamGroupFormOptions } from "features/teamGroup/hooks/useTeamGroupFormOptions";
import { _ } from "i18n";
import type { TeamGroupUpsertRequest } from "libs/apiClient";
import client from "libs/client";
import { getApiErrorMessage } from "libs/utils";
import DeleteConfirmationModal from "pages/productManagement/components/DeleteConfirmationModal";
import NotFoundPage from "pages/errorPages/NotFoundPage";
import { useToast } from "ui-kit";
import "style/discoverPage.css";
import "features/subscription/SubscriptionEditPage.css";

export function TeamGroupEditPage() {
	const { id } = useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { addErrorToast, addSuccessToast } = useToast();

	const isNew = !id || id === "new";
	const teamGroupId = !isNew ? Number.parseInt(id, 10) : undefined;
	const isValidId = teamGroupId !== undefined && !Number.isNaN(teamGroupId);

	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);

	const { discoverClubOptions, discoverTeamOptions, isLoading: isOptionsLoading } = useTeamGroupFormOptions();

	const { data: teamGroupResponse, isLoading: isTeamGroupLoading, error: teamGroupError } = client.getTeamGroup.useQuery(
		{ id: teamGroupId ?? 0 },
		{
			enabled: isValidId,
			retry: false,
		},
	);
	const teamGroup = teamGroupResponse?.data;

	const createMutation = client.postTeamGroup.useMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["getTeamGroups"] });
		},
	});
	const updateMutation = client.putTeamGroup.useMutation({
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: ["getTeamGroups"] });
			queryClient.invalidateQueries({ queryKey: ["getTeamGroup", { id: variables.id }] });
		},
	});
	const deleteMutation = client.deleteTeamGroup.useMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["getTeamGroups"] });
		},
	});

	useEffect(() => {
		setScrollBehavior();
	}, []);

	const handleSubmit = useCallback(
		async (payload: TeamGroupUpsertRequest) => {
			setSubmitError(null);
			try {
				if (isNew) {
					const response = await createMutation.mutateAsync({ body: payload });
					if (response?.data) {
						addSuccessToast(response.data);
					}
					navigate("/team-group", { state: { showGameCountPendingNotice: true } });
					return;
				}

				if (isValidId === false) {
					return;
				}

				const response = await updateMutation.mutateAsync({ id: teamGroupId, body: payload });
				if (response?.data) {
					addSuccessToast(response.data);
				}
				navigate("/team-group", { state: { showGameCountPendingNotice: true } });
			} catch (error: unknown) {
				setSubmitError(extractMutationErrorMessage(error));
			}
		},
		[addSuccessToast, createMutation, isNew, isValidId, navigate, teamGroupId, updateMutation],
	);

	const handleDelete = useCallback(async () => {
		if (isValidId === false) {
			return;
		}

		try {
			const response = await deleteMutation.mutateAsync({ id: teamGroupId });
			if (response?.data) {
				addSuccessToast(response.data);
			}
			navigate("/team-group");
		} catch (error: unknown) {
			addErrorToast(getApiErrorMessage(extractMutationErrorMessage(error)));
		} finally {
			setShowDeleteModal(false);
		}
	}, [addErrorToast, addSuccessToast, deleteMutation, isValidId, navigate, teamGroupId]);

	if (!isNew && isValidId === false) {
		return <NotFoundPage />;
	}

	if (!isNew && teamGroupError) {
		const status = (teamGroupError as { response?: { status?: number } })?.response?.status;
		if (status === 404) {
			return <NotFoundPage />;
		}
	}

	const isLoading = isOptionsLoading || (!isNew && isTeamGroupLoading);
	const isSubmitting = createMutation.isLoading || updateMutation.isLoading;

	const pageTitle = isNew
		? "Add Team Group"
		: `Team Group #${teamGroup?.id ?? teamGroupId}`;

	return (
		<div className="subscriptionEditPage-container">
			<div className="pageHeading">
				<div>
					<Link to="/team-group" className="subscriptionEditPage-backLink">
						← Team Groups Management
					</Link>
					<h1>{pageTitle}</h1>
				</div>
				{!isNew && (
					<button type="button" className="btn btn--red" onClick={() => setShowDeleteModal(true)}>
						{_("btn.delete")}
					</button>
				)}
			</div>

			{isLoading ? (
				<Loader />
			) : (
				<div className="discover-card">
					<TeamGroupForm
						row={isNew ? undefined : teamGroup}
						discoverClubOptions={discoverClubOptions}
						discoverTeamOptions={discoverTeamOptions}
						submitError={submitError}
						isSubmitting={isSubmitting}
						onSubmit={handleSubmit}
					/>
				</div>
			)}

			<DeleteConfirmationModal
				show={showDeleteModal}
				title={teamGroup?.name ?? String(teamGroupId ?? "")}
				onClose={() => setShowDeleteModal(false)}
				onDelete={handleDelete}
			/>
		</div>
	);
}

function extractMutationErrorMessage(error: unknown): string {
	if (error && typeof error === "object") {
		const err = error as {
			json?: string | { message?: string };
			message?: string;
		};
		if (typeof err.json === "string") {
			return err.json;
		}
		if (err.json?.message) {
			return err.json.message;
		}
		if (err.message) {
			return err.message;
		}
	}
	return _("message.error.generic");
}

export default TeamGroupEditPage;
